import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { TurnosProduccionApi } from "../../../api/TurnosProduccionApi";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import { getErrorFromResponse } from "../../../../../../../../../../utility/utils";
import Select from "react-select";
import moment from "moment";
import {
    TURNO_PRODUCCION,
    turnos_disponibles_produccion,
} from "../../../../../../../../../../utility/constants";
import { LOCALE_CALENDARIO_DOMINGO } from "../../../../../../helpers/dateRange";
import { useFechaHoraReloj } from "../../../../../../hooks/useFechaHoraReloj";
import { CONTEXTO_RELOJ_PRODUCCION } from "../../../../../../components/RelojDesfase/contextosReloj";

// permite que el calendario se reubique dentro de la pantalla en vez de salirse
const POPPER_MODIFIERS_CALENDARIO = {
    preventOverflow: {
        enabled: true,
        escapeWithReference: false,
        boundariesElement: "viewport",
        padding: 8,
    },
};

// Día en que inicia el turno que corresponde a la fecha y hora del reloj: de
// madrugada, el nocturno en curso empezó el día anterior
const obtenerDiaInicioTurno = (fechaHoraReloj, turno) => {
    const dia = moment(fechaHoraReloj).startOf("day");

    if (turno === TURNO_PRODUCCION.NOCTURNO && fechaHoraReloj.hour() < 6) {
        return dia.subtract(1, "day");
    }

    return dia;
};

export const FormInicioProd = (props) => {
    const { nombreUsuario, turnoActual, GetTurnoActual } = props;

    const [modoEdicion, setModoEdicion] = useState(false);

    // el turno nuevo se propone con la fecha y hora del reloj de producción
    const relojProduccion = useFechaHoraReloj(CONTEXTO_RELOJ_PRODUCCION);

    // formulario
    const {
        control,
        register: formRegister,
        formState: { errors },
        getValues: formGetValues,
        setValue: formSetValue,
        watch: formWatch,
        trigger: validateForm,
        reset: resetForm,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            operador_bascula: null,
            fecha_final: null,
        },
    });

    useEffect(() => {
        if (turnoActual) {
            formSetValue("operador_bascula", turnoActual.operador_bascula);
            formSetValue(
                "operador_montacargas",
                turnoActual.operador_montacargas,
            );
            formSetValue(
                "turno",
                turnos_disponibles_produccion.find(
                    (t) => t.value === turnoActual.turno,
                ),
            );
            formSetValue("fecha_inicial", moment(turnoActual.fecha_inicial));
            formSetValue("fecha_final", moment(turnoActual.fecha_final));
        } else {
            resetForm();
        }
    }, [turnoActual]);

    const turnoSeleccionado = formWatch("turno");

    useEffect(() => {
        // si fue consulta de un turno existente y no está en modo edición, no setear fechas automáticamente
        if (turnoActual && !modoEdicion) return;
        if (!turnoSeleccionado) return;

        // al cambiar de diurno a nocturno solo cambia el horario: el día lo
        // sigue definiendo la fecha que ya se hubiera configurado, y solo se
        // toma del reloj de producción cuando todavía no hay ninguna. Se lee
        // con getValues y no con watch para no volver a disparar este efecto
        // al escribirla
        const fechaConfigurada = formGetValues("fecha_inicial");

        const diaBase = fechaConfigurada
            ? moment(fechaConfigurada).startOf("day")
            : obtenerDiaInicioTurno(
                  relojProduccion.obtenerFechaHora(),
                  turnoSeleccionado.value,
              );

        let fechaInicio;
        let fechaFin;

        if (turnoSeleccionado.value === TURNO_PRODUCCION.DIURNO) {
            fechaInicio = moment(diaBase).hour(6); // 06:00 del día base
            fechaFin = moment(diaBase).hour(18); // 18:00 del día base
        }

        if (turnoSeleccionado.value === TURNO_PRODUCCION.NOCTURNO) {
            fechaInicio = moment(diaBase).hour(18); // 18:00 del día base
            fechaFin = moment(diaBase).add(1, "day").hour(6); // 06:00 del día siguiente
        }

        formSetValue("fecha_inicial", fechaInicio);
        formSetValue("fecha_final", fechaFin);

        // las fechas nuevas siempre son coherentes entre sí, así que se
        // revalidan solo ellas para quitar el error que hubiera quedado de las
        // anteriores, sin marcar como obligatorios los campos aún vacíos
        validateForm(["fecha_inicial", "fecha_final"]);
    }, [turnoSeleccionado]);

    // autoselección de turno al cargar el componente por primera vez, si no
    // hay turno actual. Espera al reloj de producción, porque el turno se
    // propone según su fecha y hora
    useEffect(() => {
        if (turnoActual) return; // no tocar si ya hay turno cargado
        if (turnoSeleccionado) return; // solo al cargar el componente
        if (!relojProduccion.cargado) return;

        const hora = relojProduccion.obtenerFechaHora().hour();

        let turnoAuto;

        if (hora >= 6 && hora < 18) {
            turnoAuto = TURNO_PRODUCCION.DIURNO;
        } else {
            turnoAuto = TURNO_PRODUCCION.NOCTURNO;
        }

        const turnoObj = turnos_disponibles_produccion.find(
            (t) => t.value === turnoAuto,
        );

        formSetValue("turno", turnoObj);
    }, [relojProduccion.cargado]);

    const { iniciarTurno, actualizarTurno, cargando } = TurnosProduccionApi();

    const GetSubmitData = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        const operador_bascula = formGetValues("operador_bascula");
        const operador_montacargas = formGetValues("operador_montacargas");
        const turno = formGetValues("turno");
        const fecha_inicial = formGetValues("fecha_inicial");
        const fecha_final = formGetValues("fecha_final");

        const data = {
            operador_bascula: operador_bascula,
            operador_montacargas: operador_montacargas,
            turno: turno ? turno.value : null,
            fecha_inicial: fecha_inicial,
            fecha_final: fecha_final,
        };

        return data;
    };

    const IniciarTurno = async () => {
        try {
            const data = await GetSubmitData();
            if (!data) return;

            await iniciarTurno(data);

            NotificationManager.success(
                "Turno de producción creado correctamente",
                "Éxito",
                3000,
            );

            GetTurnoActual();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al crear turno de producción",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const ActualizarTurno = async () => {
        try {
            const data = await GetSubmitData();
            if (!data) return;

            await actualizarTurno(data);

            NotificationManager.success(
                "Turno de producción actualizado correctamente",
                "Éxito",
                3000,
            );

            setModoEdicion(false);
            GetTurnoActual();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al actualizar turno de producción",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const GetBtnAccion = () => {
        if (!turnoActual && !modoEdicion) {
            return (
                <button
                    className="btn btn-primary text-nowrap mt-auto"
                    type="submit"
                    // onClick={IniciarTurno}
                    disabled={cargando}
                >
                    Comenzar turno
                </button>
            );
        } else if (turnoActual && !modoEdicion) {
            return (
                <button
                    className="btn btn-primary text-nowrap mt-auto"
                    type="submit"
                    // onClick={() => setModoEdicion(true)}
                    disabled={cargando}
                >
                    Editar
                </button>
            );
        } else if (modoEdicion) {
            return (
                <button
                    className="btn btn-primary text-nowrap mt-auto"
                    type="submit"
                    // onClick={ActualizarTurno}
                    disabled={cargando}
                >
                    Guardar
                </button>
            );
        } else {
            return null;
        }
    };

    const fechaInicial = formWatch("fecha_inicial");
    const fechaFinal = formWatch("fecha_final");

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();

                if (!turnoActual && !modoEdicion) {
                    IniciarTurno();
                } else if (turnoActual && !modoEdicion) {
                    setModoEdicion(true);
                } else if (modoEdicion) {
                    ActualizarTurno();
                }
            }}
            autoComplete="on"
        >
            {turnoActual && <h5 className="mb-3 text-center">Turno Actual</h5>}
            <div className="formulario-fields-row gap-1rem mt-2 flex-wrap">
                <div className="formulario-field min-w-150">
                    <label htmlFor="supervisor" className="text-nowrap">
                        {turnoActual && turnoActual.supervisores
                            ? "Supervisores"
                            : "Supervisor"}
                    </label>

                    <input
                        type="text"
                        className="form-control"
                        disabled
                        value={
                            turnoActual && turnoActual.supervisores
                                ? turnoActual.supervisores
                                : nombreUsuario
                        }
                    />
                </div>

                <div className="formulario-field min-w-150">
                    <label htmlFor="operador_bascula" className="text-nowrap">
                        Op. báscula
                    </label>

                    <input
                        type="text"
                        autoComplete="organization-title"
                        name="operador_bascula"
                        {...formRegister("operador_bascula", {
                            required: "Este campo es obligatorio",
                        })}
                        className="form-control"
                        disabled={!!turnoActual && !modoEdicion}
                    />
                    {errors.operador_bascula && (
                        <small className="text-danger">
                            {errors.operador_bascula.message}
                        </small>
                    )}
                </div>

                <div className="formulario-field min-w-150">
                    <label
                        htmlFor="operador_montacargas"
                        className="text-nowrap"
                    >
                        Op. montacargas
                    </label>

                    <input
                        type="text"
                        autoComplete="organization-title"
                        name="operador_montacargas"
                        {...formRegister("operador_montacargas", {
                            required: "Este campo es obligatorio",
                        })}
                        className="form-control"
                        disabled={!!turnoActual && !modoEdicion}
                    />
                    {errors.operador_montacargas && (
                        <small className="text-danger">
                            {errors.operador_montacargas.message}
                        </small>
                    )}
                </div>

                <div className="formulario-field min-w-150">
                    <label htmlFor="turno" className="text-nowrap">
                        Turno
                    </label>

                    <Controller
                        name="turno"
                        control={control}
                        rules={{
                            required: "Este campo es obligatorio",
                        }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                className="react-select-container"
                                backspaceRemovesValue={false}
                                isSearchable
                                options={turnos_disponibles_produccion}
                                placeholder="Seleccionar"
                                isDisabled={!!turnoActual && !modoEdicion}
                            />
                        )}
                    />
                    {errors.turno && (
                        <small className="text-danger">
                            {errors.turno.message}
                        </small>
                    )}
                </div>

                <div className="d-flex gap-1rem flex-1">
                    <div className="formulario-field min-w-150">
                        <label htmlFor="fecha_inicio" className="text-nowrap">
                            Fecha inicio
                        </label>

                        <Controller
                            name="fecha_inicial"
                            control={control}
                            rules={{
                                required: "La fecha inicial es obligatoria",
                                validate: (value) => {
                                    const finalActual =
                                        formGetValues("fecha_final");

                                    if (!finalActual) return true;

                                    return (
                                        new Date(value) <
                                            new Date(finalActual) ||
                                        "La fecha inicial debe ser menor a la fecha final"
                                    );
                                },
                            }}
                            render={({ field }) => (
                                <DatePicker
                                    selected={field.value}
                                    onChange={(date) => {
                                        field.onChange(date);
                                    }}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="DD/MM/YYYY HH:mm"
                                    showPopperArrow={false}
                                    placeholderText="Fecha de fin"
                                    autoFocus={false}
                                    disabled={!!turnoActual && !modoEdicion}
                                    popperModifiers={
                                        POPPER_MODIFIERS_CALENDARIO
                                    }
                                    maxDate={
                                        fechaFinal ? moment(fechaFinal) : null
                                    }
                                    locale={LOCALE_CALENDARIO_DOMINGO}
                                />
                            )}
                        />
                        {errors.fecha_inicial && (
                            <small className="text-danger">
                                {errors.fecha_inicial.message}
                            </small>
                        )}
                    </div>

                    <div className="formulario-field min-w-150">
                        <label htmlFor="fecha_fin" className="text-nowrap">
                            Fecha fin
                        </label>

                        <Controller
                            name="fecha_final"
                            control={control}
                            rules={{
                                required: "La fecha final es obligatoria",
                                validate: (value) => {
                                    const inicialActual =
                                        formGetValues("fecha_inicial");

                                    if (!inicialActual) return true;

                                    return (
                                        new Date(value) >
                                            new Date(inicialActual) ||
                                        "La fecha final debe ser mayor a la fecha inicial"
                                    );
                                },
                            }}
                            render={({ field }) => (
                                <DatePicker
                                    selected={field.value}
                                    onChange={(date) => {
                                        field.onChange(date);
                                    }}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="DD/MM/YYYY HH:mm"
                                    showPopperArrow={false}
                                    placeholderText="Fecha de fin"
                                    autoFocus={false}
                                    disabled={!!turnoActual && !modoEdicion}
                                    popperPlacement="bottom-end"
                                    popperModifiers={
                                        POPPER_MODIFIERS_CALENDARIO
                                    }
                                    minDate={
                                        fechaInicial
                                            ? moment(fechaInicial)
                                            : null
                                    }
                                    locale={LOCALE_CALENDARIO_DOMINGO}
                                />
                            )}
                        />
                        {errors.fecha_final && (
                            <small className="text-danger">
                                {errors.fecha_final.message}
                            </small>
                        )}
                    </div>
                </div>

                <div
                    className="formulario-field mb-0 ml-auto no-flex"
                    style={{ maxHeight: "57px" }}
                >
                    {GetBtnAccion()}
                </div>
            </div>
        </form>
    );
};
